import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell, LineChart, Line,
  BarChart, Bar,
} from 'recharts'
import {
  TrendingUp, TrendingDown, Euro, Package, BarChart3, Loader2,
  Filter, ArrowUpDown, Percent, ShoppingCart, FileText,
  ArrowUp, ArrowDown, Minus, Layers, Zap, AlertTriangle, Calendar,
  ChevronDown, ChevronUp,
} from 'lucide-react'

// ==================== Types ====================

interface PeriodRow {
  period: string
  total_quantity: number
  total_revenue: number
  total_cost: number
  total_profit: number
  num_invoices: number
  num_products: number
  margin_percent: number
  profit_per_unit?: number
  prev_revenue?: number | null
  prev_profit?: number | null
  prev_quantity?: number | null
  revenue_change_pct?: number | null
  revenue_change_abs?: number | null
  profit_change_pct?: number | null
  profit_change_abs?: number | null
  ytd_revenue?: number
  ytd_profit?: number
  ytd_quantity?: number
  equiv_period?: boolean
}

interface Totals {
  total_quantity: number
  total_revenue: number
  total_cost: number
  total_profit: number
  margin_percent: number
  equiv_period?: boolean
  months_compared?: number
  ejercicio?: string
}

interface SummaryResponse {
  periods: PeriodRow[]
  totals: Totals
  prev_totals: Totals | null
  date_from: string | null
  date_to: string | null
}

interface FamilyOption { family_code: string; family_name: string }

interface ProductRow {
  reference: string
  description: string | null
  family_code: string | null
  family_name: string | null
  ejercicio: string
  total_quantity: number
  total_revenue: number
  total_cost: number
  total_profit: number
  margin_percent: number
  num_invoices: number
  profit_per_unit?: number
  prev_revenue?: number | null
  prev_profit?: number | null
  prev_quantity?: number | null
  revenue_change_pct?: number | null
  profit_change_pct?: number | null
}

interface ProductResponse { products: ProductRow[]; total: number }

interface FamilyBreakdown {
  family_code: string
  family_name: string
  value: number
  total_revenue: number
  total_profit: number
  total_quantity: number
  num_products: number
  margin_percent: number
  profit_per_unit?: number
  contribution_profit_pct?: number
  contribution_revenue_pct?: number
  prev_revenue?: number | null
  prev_profit?: number | null
  revenue_change_pct?: number | null
  profit_change_pct?: number | null
  revenue_change_abs?: number | null
  profit_change_abs?: number | null
}

interface YoyMonth {
  month: string
  total_quantity: number
  total_revenue: number
  total_cost: number
  total_profit: number
  margin_percent: number
}

interface TrendProduct {
  reference: string
  description: string
  family_name: string
  prev_quantity: number
  prev_revenue: number
  curr_quantity: number
  curr_revenue: number
  curr_profit: number
  growth_pct: number
  revenue_growth_pct?: number
  monthly_data?: { month: string; qty: number; revenue: number }[]
}

interface TrendsResponse {
  growing: TrendProduct[]
  declining: TrendProduct[]
  dying: TrendProduct[]
  ejercicio: string
  prev_ejercicio: string
}

interface ZeroCostProduct {
  reference: string
  ejercicio: string
  total_quantity: number
  total_revenue: number
  description: string | null
  family_name: string | null
}

interface ZeroCostAlert {
  count: number
  total_revenue_affected: number
  products: ZeroCostProduct[]
}

type PeriodType = 'yearly' | 'quarterly' | 'monthly'
type SortField = 'profit' | 'revenue' | 'quantity' | 'margin'
type ViewTab = 'summary' | 'families' | 'products' | 'trends' | 'yoy'

const CURRENT_YEAR = new Date().getFullYear().toString()
const YEARS = Array.from({ length: 6 }, (_, i) => (parseInt(CURRENT_YEAR) - i).toString())

const PIE_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#94a3b8',
]

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const MONTH_FULL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

// ==================== Formatters ====================

function fmtCur(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

function fmtCurFull(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v)
}

function fmtNum(v: number): string {
  return new Intl.NumberFormat('es-ES').format(v)
}

function fmtPeriod(period: string, type: PeriodType): string {
  if (type === 'yearly') return period
  if (type === 'quarterly') { const [y, q] = period.split('-'); return `${q} ${y}` }
  const [y, m] = period.split('-')
  return `${MONTH_NAMES[parseInt(m) - 1]} ${y}`
}

function fmtDateRange(from: string | null, to: string | null): string | null {
  if (!from || !to) return null
  const p = (d: string) => { const [y, m] = d.split('-'); return { y, m: MONTH_FULL[parseInt(m) - 1] } }
  const f = p(from), t = p(to)
  return f.y === t.y ? `${f.m} - ${t.m} ${f.y}` : `${f.m} ${f.y} - ${t.m} ${t.y}`
}

function yoyPct(curr: number, prev: number): number | null {
  if (!prev || prev === 0) return null
  return ((curr - prev) / Math.abs(prev)) * 100
}

// ==================== Main Component ====================

export default function ProfitReportPage() {
  const [period, setPeriod] = useState<PeriodType>('monthly')
  const [ejercicio, setEjercicio] = useState<string>(CURRENT_YEAR)
  const [familyCode, setFamilyCode] = useState<string>('')
  const [viewTab, setViewTab] = useState<ViewTab>('summary')
  const [sortBy, setSortBy] = useState<SortField>('profit')
  const [productPage, setProductPage] = useState(0)
  const PRODUCT_LIMIT = 50

  // ---- Data fetching ----

  const familiesQuery = useQuery({
    queryKey: ['profit-families'],
    queryFn: async () => (await axios.get<{ families: FamilyOption[] }>('/api/profit-report/families')).data.families,
  })

  const summaryQuery = useQuery({
    queryKey: ['profit-summary', period, ejercicio, familyCode],
    queryFn: async () => {
      const params: Record<string, string> = { period }
      if (period !== 'yearly') params.ejercicio = ejercicio
      if (familyCode) params.family_code = familyCode
      return (await axios.get<SummaryResponse>('/api/profit-report/summary', { params })).data
    },
  })

  const familyBreakdownQuery = useQuery({
    queryKey: ['profit-families-breakdown', ejercicio, familyCode],
    queryFn: async () => {
      const params: Record<string, string> = { metric: 'profit', limit: '8' }
      if (ejercicio) params.ejercicio = ejercicio
      return (await axios.get<{ families: FamilyBreakdown[]; total_families: number }>('/api/profit-report/family-breakdown', { params })).data
    },
    enabled: viewTab === 'summary' || viewTab === 'families',
  })

  const productsQuery = useQuery({
    queryKey: ['profit-products', ejercicio, familyCode, sortBy, productPage],
    queryFn: async () => {
      const params: Record<string, string> = {
        order_by: sortBy, limit: PRODUCT_LIMIT.toString(), offset: (productPage * PRODUCT_LIMIT).toString(),
      }
      if (ejercicio) params.ejercicio = ejercicio
      if (familyCode) params.family_code = familyCode
      return (await axios.get<ProductResponse>('/api/profit-report/by-product', { params })).data
    },
    enabled: viewTab === 'products',
  })

  const yoyQuery = useQuery({
    queryKey: ['profit-yoy', ejercicio, familyCode],
    queryFn: async () => {
      const prevYear = String(parseInt(ejercicio) - 1)
      const params: Record<string, string> = { ejercicios: `${prevYear},${ejercicio}` }
      if (familyCode) params.family_code = familyCode
      return (await axios.get<{ years: Record<string, YoyMonth[]> }>('/api/profit-report/yoy', { params })).data
    },
    enabled: viewTab === 'yoy' || viewTab === 'summary',
  })

  const trendsQuery = useQuery({
    queryKey: ['profit-trends', ejercicio, familyCode],
    queryFn: async () => {
      const params: Record<string, string> = { ejercicio }
      if (familyCode) params.family_code = familyCode
      return (await axios.get<TrendsResponse>('/api/profit-report/product-trends', { params })).data
    },
    enabled: viewTab === 'trends',
  })

  const zeroCostQuery = useQuery({
    queryKey: ['profit-zero-cost', ejercicio],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (ejercicio) params.ejercicio = ejercicio
      return (await axios.get<ZeroCostAlert>('/api/profit-report/zero-cost-alert', { params })).data
    },
  })

  const [zeroCostExpanded, setZeroCostExpanded] = useState(false)

  // Expenses data for net profit calculation
  const expensesQuery = useQuery({
    queryKey: ['expenses-for-profit', ejercicio],
    queryFn: async () => {
      const from = `${ejercicio}-01`
      const to = `${ejercicio}-12`
      return (await axios.get<{ total: number; months_count: number; by_category: { code: string; name: string; total: number }[] }>(
        '/api/expenses/for-period', { params: { year_month_from: from, year_month_to: to } }
      )).data
    },
    enabled: period !== 'yearly',
  })

  const data = summaryQuery.data
  const totals = data?.totals
  const prevTotals = data?.prev_totals
  const periods = data?.periods || []

  const selectedFamilyName = useMemo(() => {
    if (!familyCode || !familiesQuery.data) return null
    return familiesQuery.data.find(f => f.family_code === familyCode)?.family_name || familyCode
  }, [familyCode, familiesQuery.data])

  // ==================== RENDER ====================

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary-600" />
            Informe de Beneficios
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Analisis de beneficio bruto por periodos y familias
            {selectedFamilyName && <span className="ml-1 text-primary-600 font-medium"> — {selectedFamilyName}</span>}
            {data?.date_from && data?.date_to && <span className="ml-1 text-slate-400">({fmtDateRange(data.date_from, data.date_to)})</span>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Periodo</label>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              {(['monthly', 'quarterly', 'yearly'] as PeriodType[]).map(p => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    period === p ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}>
                  {p === 'monthly' ? 'Mensual' : p === 'quarterly' ? 'Trimestral' : 'Anual'}
                </button>
              ))}
            </div>
          </div>

          {period !== 'yearly' && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Ejercicio</label>
              <select value={ejercicio} onChange={e => setEjercicio(e.target.value)} className="input py-2 pr-8">
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}

          <div className="min-w-[200px]">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              <Filter className="w-3 h-3 inline mr-1" />Familia
            </label>
            <select value={familyCode} onChange={e => setFamilyCode(e.target.value)} className="input py-2 pr-8">
              <option value="">Todas las familias</option>
              {familiesQuery.data?.map(f => (
                <option key={f.family_code} value={f.family_code}>{f.family_name || f.family_code}</option>
              ))}
            </select>
          </div>

          {familyCode && (
            <button onClick={() => setFamilyCode('')} className="text-sm text-slate-500 hover:text-primary-600 underline pb-2">
              Limpiar filtro
            </button>
          )}
        </div>
      </div>

      {/* Loading / Error */}
      {summaryQuery.isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-slate-500">Cargando datos...</span>
        </div>
      )}
      {summaryQuery.isError && (
        <div className="card p-6 text-center text-danger-600">Error al cargar los datos.</div>
      )}

      {totals && (
        <>
          {/* KPI Cards with YoY */}
          {prevTotals?.equiv_period && (
            <div className="text-xs text-slate-400 dark:text-slate-500 italic">
              Comparando con mismo periodo de {prevTotals.ejercicio} ({prevTotals.months_compared} meses)
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiCard label="Ingresos" value={fmtCur(totals.total_revenue)} icon={<Euro className="w-5 h-5" />}
              color="blue" yoyPct={yoyPct(totals.total_revenue, prevTotals?.total_revenue || 0)} prevLabel={prevTotals ? fmtCur(prevTotals.total_revenue) : undefined} />
            <KpiCard label="Coste" value={fmtCur(totals.total_cost)} icon={<ShoppingCart className="w-5 h-5" />}
              color="orange" yoyPct={yoyPct(totals.total_cost, prevTotals?.total_cost || 0)} invertTrend prevLabel={prevTotals ? fmtCur(prevTotals.total_cost) : undefined} />
            <KpiCard label="Beneficio Bruto" value={fmtCur(totals.total_profit)} icon={<TrendingUp className="w-5 h-5" />}
              color="green" highlight yoyPct={yoyPct(totals.total_profit, prevTotals?.total_profit || 0)} prevLabel={prevTotals ? fmtCur(prevTotals.total_profit) : undefined} />
            <KpiCard label="Margen" value={`${totals.margin_percent.toFixed(1)}%`} icon={<Percent className="w-5 h-5" />}
              color="purple" yoyDiff={prevTotals ? totals.margin_percent - prevTotals.margin_percent : null} prevLabel={prevTotals ? `${prevTotals.margin_percent.toFixed(1)}%` : undefined} />
            <KpiCard label="Uds. Vendidas" value={fmtNum(totals.total_quantity)} icon={<Package className="w-5 h-5" />}
              color="slate" yoyPct={yoyPct(totals.total_quantity, prevTotals?.total_quantity || 0)} prevLabel={prevTotals ? fmtNum(prevTotals.total_quantity) : undefined} />
          </div>

          {/* Net Profit Summary */}
          {expensesQuery.data && expensesQuery.data.total > 0 && period !== 'yearly' && (
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  Beneficio Neto {ejercicio}
                </h3>
                <a href="/reports/expenses" className="text-xs text-slate-400 hover:text-white underline">Ver gastos</a>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Beneficio Bruto</p>
                  <p className="text-xl font-bold text-emerald-400">{fmtCur(totals.total_profit)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Gastos Operativos</p>
                  <p className="text-xl font-bold text-red-400">-{fmtCur(expensesQuery.data.total)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Beneficio Neto</p>
                  <p className={`text-xl font-bold ${totals.total_profit - expensesQuery.data.total >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                    {fmtCur(totals.total_profit - expensesQuery.data.total)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Margen Neto</p>
                  <p className={`text-xl font-bold ${totals.total_profit - expensesQuery.data.total >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                    {totals.total_revenue > 0 ? ((totals.total_profit - expensesQuery.data.total) / totals.total_revenue * 100).toFixed(1) : '0'}%
                  </p>
                </div>
              </div>
              {expensesQuery.data.by_category.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700 flex flex-wrap gap-3">
                  {expensesQuery.data.by_category.slice(0, 5).map(c => (
                    <span key={c.code} className="text-xs text-slate-400">
                      {c.name}: <span className="text-slate-300">{fmtCur(c.total)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Zero cost alert */}
          {zeroCostQuery.data && zeroCostQuery.data.count > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <button
                type="button"
                onClick={() => setZeroCostExpanded(!zeroCostExpanded)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <div className="text-left">
                    <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                      {zeroCostQuery.data.count} articulo{zeroCostQuery.data.count !== 1 ? 's' : ''} vendido{zeroCostQuery.data.count !== 1 ? 's' : ''} sin precio de coste
                    </span>
                    <span className="text-sm text-amber-600 dark:text-amber-400 ml-2">
                      ({fmtCurFull(zeroCostQuery.data.total_revenue_affected)} en ventas excluidas del calculo de margen)
                    </span>
                  </div>
                </div>
                {zeroCostExpanded ? (
                  <ChevronUp className="w-4 h-4 text-amber-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-amber-500" />
                )}
              </button>
              {zeroCostExpanded && (
                <div className="mt-3 max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-200 dark:border-amber-700">
                        <th className="text-left py-1.5 px-2 text-xs font-medium text-amber-700 dark:text-amber-400">Referencia</th>
                        <th className="text-left py-1.5 px-2 text-xs font-medium text-amber-700 dark:text-amber-400">Descripcion</th>
                        <th className="text-left py-1.5 px-2 text-xs font-medium text-amber-700 dark:text-amber-400">Familia</th>
                        <th className="text-right py-1.5 px-2 text-xs font-medium text-amber-700 dark:text-amber-400">Uds.</th>
                        <th className="text-right py-1.5 px-2 text-xs font-medium text-amber-700 dark:text-amber-400">Ingresos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100 dark:divide-amber-800">
                      {zeroCostQuery.data.products.map((p) => (
                        <tr key={`${p.reference}-${p.ejercicio}`}>
                          <td className="py-1.5 px-2 font-mono text-xs text-amber-800 dark:text-amber-300">{p.reference}</td>
                          <td className="py-1.5 px-2 text-amber-700 dark:text-amber-400 max-w-[200px] truncate">{p.description || '—'}</td>
                          <td className="py-1.5 px-2 text-xs text-amber-600 dark:text-amber-500">{p.family_name || '—'}</td>
                          <td className="py-1.5 px-2 text-right text-amber-700 dark:text-amber-400">{fmtNum(p.total_quantity)}</td>
                          <td className="py-1.5 px-2 text-right font-medium text-amber-800 dark:text-amber-300">{fmtCurFull(p.total_revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
            {([
              ['summary', 'Resumen', <BarChart3 key="i" className="w-4 h-4" />],
              ['families', 'Familias', <Layers key="i" className="w-4 h-4" />],
              ['products', 'Productos', <FileText key="i" className="w-4 h-4" />],
              ['trends', 'Tendencias', <Zap key="i" className="w-4 h-4" />],
              ['yoy', 'Comparativa YoY', <Calendar key="i" className="w-4 h-4" />],
            ] as [ViewTab, string, React.ReactNode][]).map(([tab, label, icon]) => (
              <button key={tab} onClick={() => setViewTab(tab)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  viewTab === tab ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}>
                {icon}{label}
              </button>
            ))}
          </div>

          {/* ==================== TAB: SUMMARY ==================== */}
          {viewTab === 'summary' && (
            <div className="space-y-5">
              {/* Area Chart: Revenue vs Cost with Margin line */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Evolucion de ingresos, costes y margen</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={periods.map(p => ({
                      name: fmtPeriod(p.period, period),
                      Ingresos: p.total_revenue,
                      Coste: p.total_cost,
                      Beneficio: p.total_profit,
                      Margen: p.margin_percent,
                    }))}>
                      <defs>
                        <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={v => fmtCur(v)} width={70} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} domain={[0, 'auto']} width={45} />
                      <RechartsTooltip formatter={(value: number, name: string) => [name === 'Margen' ? `${value.toFixed(1)}%` : fmtCurFull(value), name]} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="Ingresos" stroke="#3b82f6" fill="url(#gradRevenue)" strokeWidth={2} />
                      <Area yAxisId="left" type="monotone" dataKey="Coste" stroke="#f97316" fill="url(#gradCost)" strokeWidth={2} />
                      <Line yAxisId="right" type="monotone" dataKey="Margen" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Two-column: Profit bar chart + Family donut */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Profit bars */}
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Beneficio bruto por periodo</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={periods.map(p => ({
                        name: fmtPeriod(p.period, period),
                        Beneficio: p.total_profit,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtCur(v)} width={70} />
                        <RechartsTooltip formatter={(value: number) => [fmtCurFull(value), 'Beneficio']} />
                        <Bar dataKey="Beneficio" radius={[4, 4, 0, 0]}>
                          {periods.map((p, i) => (
                            <Cell key={i} fill={p.total_profit >= 0 ? '#10b981' : '#ef4444'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Family donut */}
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Beneficio por familia</h3>
                  {familyBreakdownQuery.data ? (
                    <div className="flex items-center gap-4 h-64">
                      <div className="w-1/2 h-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={familyBreakdownQuery.data.families.map(f => ({
                              name: f.family_name, value: Math.max(0, Number(f.total_profit)),
                            }))}
                              cx="50%" cy="50%" innerRadius="45%" outerRadius="80%"
                              dataKey="value" paddingAngle={2}>
                              {familyBreakdownQuery.data.families.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(value: number) => [fmtCurFull(value), 'Beneficio']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-1/2 space-y-1.5 overflow-y-auto max-h-64">
                        {familyBreakdownQuery.data.families.map((f, i) => (
                          <div key={f.family_code} className="flex items-center gap-2 text-xs">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                            <span className="text-slate-600 dark:text-slate-400 truncate flex-1">{f.family_name}</span>
                            <span className="font-medium text-slate-900 dark:text-white whitespace-nowrap">{fmtCur(Number(f.total_profit))}</span>
                            <MarginBadge value={f.margin_percent} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                  )}
                </div>
              </div>

              {/* Data table */}
              <SummaryTable periods={periods} totals={totals} period={period} />
            </div>
          )}

          {/* ==================== TAB: FAMILIES ==================== */}
          {viewTab === 'families' && (
            <FamiliesTab ejercicio={ejercicio} familyCode={familyCode} familyBreakdownQuery={familyBreakdownQuery} selectedFamilyName={selectedFamilyName} />
          )}

          {/* ==================== TAB: PRODUCTS ==================== */}
          {viewTab === 'products' && (
            <ProductsTab productsQuery={productsQuery} sortBy={sortBy} setSortBy={setSortBy}
              productPage={productPage} setProductPage={setProductPage} PRODUCT_LIMIT={PRODUCT_LIMIT}
              ejercicio={ejercicio} selectedFamilyName={selectedFamilyName} />
          )}

          {/* ==================== TAB: TRENDS ==================== */}
          {viewTab === 'trends' && (
            <TrendsTab trendsQuery={trendsQuery} ejercicio={ejercicio} selectedFamilyName={selectedFamilyName} />
          )}

          {/* ==================== TAB: YOY ==================== */}
          {viewTab === 'yoy' && (
            <YoyTab yoyQuery={yoyQuery} ejercicio={ejercicio} />
          )}
        </>
      )}
    </div>
  )
}

// ==================== Sub-components ====================

function KpiCard({ label, value, icon, color, highlight, yoyPct: yoyVal, yoyDiff, invertTrend, prevLabel }: {
  label: string; value: string; icon: React.ReactNode; highlight?: boolean; prevLabel?: string
  color: 'blue' | 'orange' | 'green' | 'purple' | 'slate'
  yoyPct?: number | null; yoyDiff?: number | null; invertTrend?: boolean
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    orange: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
    green: 'bg-success-50 text-success-600 dark:bg-success-900/20 dark:text-success-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  }

  const changeVal = yoyDiff !== undefined && yoyDiff !== null ? yoyDiff : (yoyVal !== undefined && yoyVal !== null ? yoyVal : null)
  const isPercent = yoyDiff !== undefined && yoyDiff !== null

  let trendColor = 'text-slate-400'
  let TrendIcon = Minus
  if (changeVal !== null && Math.abs(changeVal) > 0.5) {
    const isPositive = changeVal > 0
    const isGood = invertTrend ? !isPositive : isPositive
    trendColor = isGood ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
    TrendIcon = isPositive ? ArrowUp : ArrowDown
  }

  return (
    <div className={`card p-4 ${highlight ? 'ring-2 ring-success-200 dark:ring-success-800' : ''}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`p-1.5 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-lg font-bold text-slate-900 dark:text-white">{value}</div>
      {changeVal !== null && (
        <div className={`flex items-center gap-1 mt-1 text-xs ${trendColor}`}>
          <TrendIcon className="w-3 h-3" />
          <span className="font-medium">
            {isPercent ? `${changeVal > 0 ? '+' : ''}${changeVal.toFixed(1)}pp` : `${changeVal > 0 ? '+' : ''}${changeVal.toFixed(1)}%`}
          </span>
          {prevLabel && <span className="text-slate-400 ml-1">vs {prevLabel}</span>}
        </div>
      )}
    </div>
  )
}

function MarginBadge({ value }: { value: number }) {
  const cls = value >= 40 ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
    : value >= 25 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-2xs font-medium ${cls}`}>{value.toFixed(1)}%</span>
}

function DeltaBadge({ value, suffix = '%', invert }: { value: number | null | undefined; suffix?: string; invert?: boolean }) {
  if (value === null || value === undefined) return <span className="text-slate-300 dark:text-slate-600">—</span>
  const isPositive = value > 0
  const isGood = invert ? !isPositive : isPositive
  const cls = Math.abs(value) < 0.5 ? 'text-slate-400' : isGood ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
  return (
    <span className={`inline-flex items-center gap-0.5 text-2xs font-semibold ${cls}`}>
      {Math.abs(value) >= 0.5 && (isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      {value > 0 ? '+' : ''}{suffix === '%' ? `${value.toFixed(1)}%` : fmtCurFull(value)}
    </span>
  )
}

function SummaryTable({ periods, totals, period }: { periods: PeriodRow[]; totals: Totals; period: PeriodType }) {
  const hasYoY = periods.some(p => p.prev_revenue !== null && p.prev_revenue !== undefined)
  const hasYtd = periods.some(p => p.ytd_revenue !== undefined)
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Periodo</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Uds.</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ingresos</th>
              {hasYoY && <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">vs Ant.</th>}
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Beneficio</th>
              {hasYoY && <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">vs Ant.</th>}
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Margen</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Benef/Ud</th>
              {hasYtd && <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Acum. Benef.</th>}
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Prods.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {periods.map(p => (
              <tr key={p.period} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                  {fmtPeriod(p.period, period)}
                  {p.equiv_period && <span className="ml-1 text-2xs text-amber-500" title="Comparacion con mismo periodo del año anterior">*</span>}
                </td>
                <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">{fmtNum(p.total_quantity)}</td>
                <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">{fmtCurFull(p.total_revenue)}</td>
                {hasYoY && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <DeltaBadge value={p.revenue_change_pct} />
                      <span className="text-2xs text-slate-400">{p.revenue_change_abs != null ? (p.revenue_change_abs > 0 ? '+' : '') + fmtCurFull(p.revenue_change_abs) : ''}</span>
                    </div>
                  </td>
                )}
                <td className={`px-4 py-3 text-sm text-right font-semibold ${p.total_profit >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                  {fmtCurFull(p.total_profit)}
                </td>
                {hasYoY && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <DeltaBadge value={p.profit_change_pct} />
                      <span className="text-2xs text-slate-400">{p.profit_change_abs != null ? (p.profit_change_abs > 0 ? '+' : '') + fmtCurFull(p.profit_change_abs) : ''}</span>
                    </div>
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-right"><MarginBadge value={p.margin_percent} /></td>
                <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">{p.profit_per_unit != null ? fmtCurFull(p.profit_per_unit) : '—'}</td>
                {hasYtd && <td className="px-4 py-3 text-sm text-right font-medium text-slate-700 dark:text-slate-300">{p.ytd_profit != null ? fmtCur(p.ytd_profit) : '—'}</td>}
                <td className="px-4 py-3 text-sm text-right text-slate-500">{fmtNum(p.num_products)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 dark:bg-slate-800 font-semibold">
              <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">Total</td>
              <td className="px-4 py-3 text-sm text-right text-slate-900 dark:text-white">{fmtNum(totals.total_quantity)}</td>
              <td className="px-4 py-3 text-sm text-right text-slate-900 dark:text-white">{fmtCurFull(totals.total_revenue)}</td>
              {hasYoY && <td className="px-4 py-3 text-sm text-right text-slate-500">—</td>}
              <td className={`px-4 py-3 text-sm text-right ${totals.total_profit >= 0 ? 'text-success-600' : 'text-danger-600'}`}>{fmtCurFull(totals.total_profit)}</td>
              {hasYoY && <td className="px-4 py-3 text-sm text-right text-slate-500">—</td>}
              <td className="px-4 py-3 text-sm text-right text-slate-900 dark:text-white">{totals.margin_percent.toFixed(1)}%</td>
              <td className="px-4 py-3 text-sm text-right text-slate-500">—</td>
              {hasYtd && <td className="px-4 py-3 text-sm text-right text-slate-500">—</td>}
              <td className="px-4 py-3 text-sm text-right text-slate-500">—</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {periods.some(p => p.equiv_period) && (
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800">
          <p className="text-2xs text-slate-400 italic">* Comparacion con el mismo periodo del año anterior (no año completo)</p>
        </div>
      )}
    </div>
  )
}

// ==================== FAMILIES TAB ====================

function PeriodBadge({ ejercicio, selectedFamilyName }: { ejercicio: string; selectedFamilyName?: string | null }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-xs font-medium">
        <Calendar className="w-3.5 h-3.5" />
        Ejercicio {ejercicio}
      </span>
      {selectedFamilyName && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs font-medium">
          <Filter className="w-3.5 h-3.5" />
          {selectedFamilyName}
        </span>
      )}
    </div>
  )
}

function FamiliesTab({ ejercicio, familyCode, familyBreakdownQuery, selectedFamilyName }: {
  ejercicio: string; familyCode: string; selectedFamilyName?: string | null
  familyBreakdownQuery: ReturnType<typeof useQuery<{ families: FamilyBreakdown[]; total_families: number }>>
}) {
  const [selectedMetric, setSelectedMetric] = useState<'profit' | 'revenue' | 'quantity'>('profit')

  const metricBreakdown = useQuery({
    queryKey: ['profit-family-breakdown-metric', ejercicio, selectedMetric],
    queryFn: async () => {
      const params: Record<string, string> = { metric: selectedMetric, limit: '15', ejercicio }
      return (await axios.get<{ families: FamilyBreakdown[]; total_families: number }>('/api/profit-report/family-breakdown', { params })).data
    },
  })

  const families = metricBreakdown.data?.families || familyBreakdownQuery.data?.families || []
  const maxValue = Math.max(...families.map(f => Math.abs(Number(f.value))), 1)

  return (
    <div className="space-y-5">
      {/* Period context + Metric toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodBadge ejercicio={ejercicio} selectedFamilyName={selectedFamilyName} />
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">Ordenar por:</span>
        {([['profit', 'Beneficio'], ['revenue', 'Ingresos'], ['quantity', 'Unidades']] as const).map(([key, label]) => (
          <button key={key} onClick={() => setSelectedMetric(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              selectedMetric === key ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200'
            }`}>
            {label}
          </button>
        ))}
        </div>
      </div>

      {/* Family ranking with horizontal bars */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-44">Familia</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{selectedMetric === 'profit' ? 'Beneficio' : selectedMetric === 'revenue' ? 'Ingresos' : 'Unidades'}</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Ingresos</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">% Ing.</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Beneficio</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">% Benef.</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">vs Ant.</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Margen</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">B/Ud</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Uds.</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Prods.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {families.map((f, i) => {
                const pct = (Math.abs(Number(f.value)) / maxValue) * 100
                return (
                  <tr key={f.family_code} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded flex items-center justify-center text-2xs font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}>
                          {i + 1}
                        </span>
                        <span className="text-sm font-medium text-slate-900 dark:text-white truncate">{f.family_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2.5 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap w-16 text-right">
                          {selectedMetric === 'quantity' ? fmtNum(Number(f.value)) : fmtCur(Number(f.value))}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{fmtCur(Number(f.total_revenue))}</td>
                    <td className="px-4 py-2.5 text-xs text-right text-slate-400">{f.contribution_revenue_pct != null ? `${f.contribution_revenue_pct.toFixed(1)}%` : '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium text-success-600 dark:text-success-400">{fmtCur(Number(f.total_profit))}</td>
                    <td className="px-4 py-2.5 text-xs text-right text-slate-400">{f.contribution_profit_pct != null ? `${f.contribution_profit_pct.toFixed(1)}%` : '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <DeltaBadge value={f.profit_change_pct} />
                    </td>
                    <td className="px-4 py-2.5 text-right"><MarginBadge value={f.margin_percent} /></td>
                    <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{f.profit_per_unit != null ? fmtCurFull(f.profit_per_unit) : '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-slate-500">{fmtNum(Number(f.total_quantity))}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-slate-500">{fmtNum(f.num_products)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ==================== PRODUCTS TAB ====================

function ProductsTab({ productsQuery, sortBy, setSortBy, productPage, setProductPage, PRODUCT_LIMIT, ejercicio, selectedFamilyName }: {
  productsQuery: ReturnType<typeof useQuery<ProductResponse>>
  sortBy: SortField; setSortBy: (f: SortField) => void
  productPage: number; setProductPage: (fn: (p: number) => number) => void; PRODUCT_LIMIT: number
  ejercicio: string; selectedFamilyName?: string | null
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodBadge ejercicio={ejercicio} selectedFamilyName={selectedFamilyName} />
        <div className="flex items-center gap-3">
        <ArrowUpDown className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-500">Ordenar por:</span>
        {([['profit', 'Beneficio'], ['revenue', 'Ingresos'], ['quantity', 'Cantidad'], ['margin', 'Margen']] as [SortField, string][]).map(([field, label]) => (
          <button key={field} onClick={() => { setSortBy(field); setProductPage(() => 0) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              sortBy === field ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200'
            }`}>{label}</button>
        ))}
        </div>
      </div>

      {productsQuery.isLoading && <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>}

      {productsQuery.data && (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Referencia</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Descripcion</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Uds.</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ingresos</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">vs Ant.</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Beneficio</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">vs Ant.</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Margen</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">B/Ud</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {productsQuery.data.products.map(p => (
                    <tr key={`${p.reference}-${p.ejercicio}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                      <td className="px-4 py-2.5 text-sm font-mono font-medium text-primary-600 dark:text-primary-400">{p.reference}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400 max-w-[220px] truncate" title={p.description || ''}>{p.description || '—'}</td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{fmtNum(p.total_quantity)}</td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{fmtCurFull(p.total_revenue)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <DeltaBadge value={p.revenue_change_pct} />
                      </td>
                      <td className={`px-4 py-2.5 text-sm text-right font-semibold ${p.total_profit >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                        {fmtCurFull(p.total_profit)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <DeltaBadge value={p.profit_change_pct} />
                      </td>
                      <td className="px-4 py-2.5 text-right"><MarginBadge value={p.margin_percent} /></td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-600 dark:text-slate-400">{p.profit_per_unit != null ? fmtCurFull(p.profit_per_unit) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {productsQuery.data.total > PRODUCT_LIMIT && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {productPage * PRODUCT_LIMIT + 1}–{Math.min((productPage + 1) * PRODUCT_LIMIT, productsQuery.data.total)} de {fmtNum(productsQuery.data.total)}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setProductPage(p => Math.max(0, p - 1))} disabled={productPage === 0} className="btn btn-sm btn-secondary disabled:opacity-50">Anterior</button>
                <button onClick={() => setProductPage(p => p + 1)} disabled={(productPage + 1) * PRODUCT_LIMIT >= productsQuery.data.total} className="btn btn-sm btn-secondary disabled:opacity-50">Siguiente</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ==================== TRENDS TAB ====================

function TrendsTab({ trendsQuery, ejercicio, selectedFamilyName }: {
  trendsQuery: ReturnType<typeof useQuery<TrendsResponse>>; ejercicio: string; selectedFamilyName?: string | null
}) {
  if (trendsQuery.isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  if (!trendsQuery.data) return null

  const { growing, declining, dying, prev_ejercicio } = trendsQuery.data

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodBadge ejercicio={ejercicio} selectedFamilyName={selectedFamilyName} />
        <p className="text-sm text-slate-500">Comparando {ejercicio} vs {prev_ejercicio} (mismos meses)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Growing */}
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-success-50 dark:bg-success-900/20 border-b border-success-100 dark:border-success-900/30">
            <h3 className="text-sm font-semibold text-success-700 dark:text-success-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> En crecimiento ({growing.length})
            </h3>
          </div>
          {growing.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No hay productos con crecimiento significativo (&gt;20%)</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {growing.map(p => <TrendProductRow key={p.reference} product={p} type="growing" />)}
            </div>
          )}
        </div>

        {/* Declining */}
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-danger-50 dark:bg-danger-900/20 border-b border-danger-100 dark:border-danger-900/30">
            <h3 className="text-sm font-semibold text-danger-700 dark:text-danger-400 flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> En declive ({declining.length})
            </h3>
          </div>
          {declining.length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No hay productos con caida significativa (&gt;20%)</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {declining.map(p => <TrendProductRow key={p.reference} product={p} type="declining" />)}
            </div>
          )}
        </div>
      </div>

      {/* Dying products */}
      {dying.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/30">
            <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Productos sin ventas este año ({dying.length})
            </h3>
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">Productos con &ge;5 uds. vendidas en {prev_ejercicio} pero 0 en {ejercicio}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50">
                  {['Referencia', 'Descripcion', 'Familia', `Uds. ${prev_ejercicio}`, `Ingresos ${prev_ejercicio}`].map((h, i) => (
                    <th key={h} className={`${i < 3 ? 'text-left' : 'text-right'} px-4 py-2 text-xs font-semibold text-slate-500 uppercase`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {dying.map(p => (
                  <tr key={p.reference} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="px-4 py-2 text-sm font-mono text-primary-600">{p.reference}</td>
                    <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 max-w-[250px] truncate">{p.description || '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{p.family_name || '—'}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-600">{fmtNum(p.prev_quantity)}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-600">{fmtCurFull(p.prev_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function TrendProductRow({ product: p, type }: { product: TrendProduct; type: 'growing' | 'declining' }) {
  const isGrowing = type === 'growing'
  return (
    <div className="px-4 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono font-medium text-primary-600 dark:text-primary-400">{p.reference}</span>
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
            isGrowing ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
            : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
          }`}>
            {p.growth_pct > 0 ? '+' : ''}{p.growth_pct.toFixed(0)}%
          </span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-0.5">{p.description || p.family_name || ''}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-medium text-slate-900 dark:text-white">{fmtNum(p.prev_quantity)} → {fmtNum(p.curr_quantity)} uds</div>
        <div className="text-xs text-slate-500">{fmtCur(p.prev_revenue)} → {fmtCur(p.curr_revenue)}</div>
      </div>
    </div>
  )
}

// ==================== YOY TAB ====================

function YoyTab({ yoyQuery, ejercicio }: {
  yoyQuery: ReturnType<typeof useQuery<{ years: Record<string, YoyMonth[]> }>>; ejercicio: string
}) {
  const [metric, setMetric] = useState<'total_revenue' | 'total_profit' | 'total_quantity'>('total_revenue')

  if (yoyQuery.isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  if (!yoyQuery.data) return null

  const prevYear = String(parseInt(ejercicio) - 1)
  const years = yoyQuery.data.years

  // Build chart data: 12 months, each with both years
  const chartData = Array.from({ length: 12 }, (_, i) => {
    const monthNum = String(i + 1).padStart(2, '0')
    const label = MONTH_NAMES[i]
    const prev = years[prevYear]?.find(m => m.month === monthNum)
    const curr = years[ejercicio]?.find(m => m.month === monthNum)
    return {
      name: label,
      [prevYear]: prev ? Number(prev[metric]) : 0,
      [ejercicio]: curr ? Number(curr[metric]) : 0,
    }
  })

  // Cumulative YTD
  let cumPrev = 0, cumCurr = 0
  const ytdData = chartData.map(d => {
    cumPrev += Number(d[prevYear]) || 0
    cumCurr += Number(d[ejercicio]) || 0
    return { name: d.name, [prevYear]: cumPrev, [ejercicio]: cumCurr }
  })

  const metricLabel = metric === 'total_revenue' ? 'Ingresos' : metric === 'total_profit' ? 'Beneficio' : 'Unidades'
  const formatter = metric === 'total_quantity' ? fmtNum : fmtCur

  return (
    <div className="space-y-5">
      {/* Metric toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-500">Metrica:</span>
        {([['total_revenue', 'Ingresos'], ['total_profit', 'Beneficio'], ['total_quantity', 'Unidades']] as const).map(([key, label]) => (
          <button key={key} onClick={() => setMetric(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              metric === key ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200'
            }`}>{label}</button>
        ))}
      </div>

      {/* Monthly overlay chart */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">{metricLabel} mensual: {prevYear} vs {ejercicio}</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatter(v)} width={70} />
              <RechartsTooltip formatter={(value: number, name: string) => [metric === 'total_quantity' ? fmtNum(value) : fmtCurFull(value), name]} />
              <Legend />
              <Line type="monotone" dataKey={prevYear} stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
              <Line type="monotone" dataKey={ejercicio} stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cumulative YTD chart */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">{metricLabel} acumulado (YTD)</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ytdData}>
              <defs>
                <linearGradient id="gradPrev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradCurr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatter(v)} width={70} />
              <RechartsTooltip formatter={(value: number, name: string) => [metric === 'total_quantity' ? fmtNum(value) : fmtCurFull(value), name]} />
              <Legend />
              <Area type="monotone" dataKey={prevYear} stroke="#94a3b8" fill="url(#gradPrev)" strokeWidth={2} strokeDasharray="5 5" />
              <Area type="monotone" dataKey={ejercicio} stroke="#3b82f6" fill="url(#gradCurr)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly comparison table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Mes</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{prevYear}</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{ejercicio}</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Variacion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {chartData.map((d, i) => {
                const prev = Number(d[prevYear]) || 0
                const curr = Number(d[ejercicio]) || 0
                const change = prev > 0 ? ((curr - prev) / prev * 100) : (curr > 0 ? 100 : 0)
                const hasData = prev > 0 || curr > 0
                if (!hasData) return null
                return (
                  <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="px-4 py-2.5 text-sm font-medium text-slate-900 dark:text-white">{MONTH_FULL[i]}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-slate-500">{prev > 0 ? formatter(prev) : '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-slate-900 dark:text-white font-medium">{curr > 0 ? formatter(curr) : '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-right">
                      {prev > 0 && curr > 0 ? (
                        <span className={`font-medium ${change >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                          {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
